import ReportIcon from '@mui/icons-material/Report';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { AsyncThunk } from '@reduxjs/toolkit';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ConfirmationDialog } from '../../../../components/AsyncDialog';
import { AsyncDialog, AsyncDialogRef } from '../../../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialogData } from '../../../../components/AsyncDialog/ConfirmationDialog';
import { ProtectedContent } from '../../../../components/ProtectedContent';
import { isProduction } from '../../../../constants/environment';
import { SectionCard } from '../../../../components/Section';
import COLORS from '../../../../constants/colors';
import { fetchStatuses } from '../../../../constants/fetchStatuses';
import { useInterval } from '../../../../hooks/useInterval';
import { PermissionPropsItem } from '../../../../layouts/AppPageLayout/Sidebar/types';
import { useAppDispatch } from '../../../../redux/hooks';
import {
  checkJournalStatus,
  sendOrderToCompleteQueue,
  sendOrderToJournalQueue,
  sendOrderToOrderQueue,
  sendOrderToTransferQueue,
  triggerTransferCreatedToCompleted,
} from '../../../../redux/modules/alpaca/alpaca.actions';
import { alpacaSelector } from '../../../../redux/modules/alpaca/alpaca.selectors';
import { AlpacaOrderSide, AlpacaOrderStep, IAlpacaOrder } from '../../../../types/alpaca';
import * as permissions from '../../../../utils/permissions';

interface AlpacaActionsProps {
  /** Rendered inside the same card, so dev mocks do not open a second surface. */
  children?: React.ReactNode;
  order: IAlpacaOrder;
  refresh: () => void;
}

type TransferActionData = {
  queue: string;
  fetchStatusField: string;
  permission: PermissionPropsItem;
  sendToQueue: AsyncThunk<any, any, any>;
};

const transferActionData: TransferActionData = {
  queue: 'Transfer',
  sendToQueue: sendOrderToTransferQueue,
  permission: permissions.AlpacaPermissions.sendToTransferQueue,
  fetchStatusField: 'sendOrderToTransferQueueStatus',
};
const journalActionData: TransferActionData = {
  queue: 'Journal',
  sendToQueue: sendOrderToJournalQueue,
  permission: permissions.AlpacaPermissions.sendToJournalQueue,
  fetchStatusField: 'sendOrderToJournalQueueStatus',
};
const orderActionData: TransferActionData = {
  queue: 'Order',
  sendToQueue: sendOrderToOrderQueue,
  permission: permissions.AlpacaPermissions.sendToOrderQueue,
  fetchStatusField: 'sendOrderToOrderQueueStatus',
};
const completeActionData: TransferActionData = {
  queue: 'Complete',
  sendToQueue: sendOrderToCompleteQueue,
  permission: permissions.AlpacaPermissions.sendToCompleteQueue,
  fetchStatusField: 'sendOrderToCompleteQueueStatus',
};

const mapBuyStepToActionData: Partial<Record<AlpacaOrderStep, TransferActionData>> = {
  [AlpacaOrderStep.INITIATED]: transferActionData,
  [AlpacaOrderStep.TRANSFER_CREATED]: journalActionData,
  [AlpacaOrderStep.TRANSFER_COMPLETED]: journalActionData,
  [AlpacaOrderStep.JOURNAL_COMPLETED]: orderActionData,
  [AlpacaOrderStep.ORDER_COMPLETED]: completeActionData,
};

const mapSellStepToActionData: Partial<Record<AlpacaOrderStep, TransferActionData>> = {
  [AlpacaOrderStep.INITIATED]: orderActionData,
  [AlpacaOrderStep.TRANSFER_CREATED]: completeActionData,
  [AlpacaOrderStep.TRANSFER_RECEIVED]: completeActionData,
  [AlpacaOrderStep.TRANSFER_COMPLETED]: completeActionData,
  [AlpacaOrderStep.JOURNAL_COMPLETED]: transferActionData,
  [AlpacaOrderStep.ORDER_COMPLETED]: journalActionData,
};

const actionDelay = 5000;

export function AlpacaActions({ order, refresh, children }: AlpacaActionsProps) {
  const { t } = useTranslation('apiError');
  const dispatch = useAppDispatch();
  const asyncDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const { error, ...alpacaData } = useSelector(alpacaSelector);
  const { enqueueSnackbar } = useSnackbar();
  const interval = useInterval();
  const [actionData, setActionData] = useState<TransferActionData | undefined>(undefined);
  const [useMock, setUseMock] = useState(false);
  const isDelayLoading = interval.time > 0;
  const isLoading = actionData ? alpacaData?.[actionData.fetchStatusField] === fetchStatuses.pending : false;
  const isTriggerLoading = alpacaData?.triggerTransferCreatedToCompletedStatus === fetchStatuses.pending;
  const isCheckJournalStatusLoading = alpacaData?.checkJournalStatusStatus === fetchStatuses.pending;

  const onSendToQueue = async () => {
    if (!actionData) {
      return;
    }

    const isConfirmed = await asyncDialogRef.current?.show({
      title: `Send Order To ${actionData.queue} Queue`,
      description: `Are you sure you want to send this order to Alpaca ${actionData.queue} Queue now?`,
    });

    if (isConfirmed) {
      try {
        await dispatch(actionData.sendToQueue({ madkholOrderId: order.orderId, useMock })).unwrap();

        interval.start(1000);
        refresh();

        enqueueSnackbar(`Order Sent To Journal ${actionData.queue} successfully!`, { variant: 'success' });
      } catch (err: any) {
        enqueueSnackbar(
          `Send Order To Journal ${actionData.queue} failed: ${t(err?.message)} ${JSON.stringify(err?.data)}`,
          {
            variant: 'error',
          },
        );
      }
    }
  };

  const onTriggerTransferCreatedToCompleted = async () => {
    try {
      await dispatch(triggerTransferCreatedToCompleted(order.orderId)).unwrap();
      refresh();
      enqueueSnackbar('Transfer created to completed triggered successfully.', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`Trigger failed: ${t(err?.message)} ${JSON.stringify(err?.data ?? '')}`, { variant: 'error' });
    }
  };

  const onCheckJournalStatus = async () => {
    if (!order.alpacaJournal?.journalId) {
      return;
    }

    try {
      const result = await dispatch(checkJournalStatus(order.alpacaJournal.journalId)).unwrap();

      refresh();
      enqueueSnackbar(
        result?.data?.synced
          ? `Journal status: ${result.data.alpacaStatus} (synced)`
          : `Journal status: ${result?.data?.alpacaStatus ?? 'unknown'}`,
        { variant: 'success' },
      );
    } catch (err: any) {
      enqueueSnackbar(`Check journal status failed: ${t(err?.message)} ${JSON.stringify(err?.data ?? '')}`, {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    const mapStepToActionData = order.side === AlpacaOrderSide.BUY ? mapBuyStepToActionData : mapSellStepToActionData;

    setActionData(mapStepToActionData[order.step]);
  }, [order.step]);

  React.useEffect(() => {
    if (interval.time >= actionDelay) {
      refresh();
      interval.stop();
    }
  }, [interval.time, actionDelay]);

  React.useEffect(
    () => () => {
      interval.stop();
    },
    [],
  );

  const showTriggerButton = order.step === AlpacaOrderStep.TRANSFER_CREATED && useMock;
  const showCheckJournalStatusButton =
    order.step === AlpacaOrderStep.JOURNAL_CREATED && !!order.alpacaJournal?.journalId;

  // With none of these applicable the card would render an empty controls row.
  const hasActions = Boolean(actionData) || showTriggerButton || showCheckJournalStatusButton;

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: '16px', borderRadius: '8px' }}>
          {error.message}
        </Alert>
      )}
      <SectionCard title="Actions">
        {!isProduction && (
          <Box sx={{ mb: hasActions ? '12px' : 0 }}>
            <FormControlLabel
              control={<Checkbox checked={useMock} onChange={(e) => setUseMock(e.target.checked)} color="primary" />}
              label="Use mock"
            />
          </Box>
        )}
        {!hasActions && (
          <Typography sx={{ fontSize: '0.875rem', color: COLORS.LIGHT_GRAY }}>
            No actions available at this step.
          </Typography>
        )}
        <Box sx={{ display: hasActions ? 'flex' : 'none', flexWrap: 'wrap', gap: '12px' }}>
          {actionData && (
            <ProtectedContent items={[actionData.permission]}>
              <LoadingButton
                type="button"
                color="warning"
                variant="contained"
                endIcon={<ReportIcon />}
                loading={isLoading || isDelayLoading}
                loadingPosition="end"
                onClick={onSendToQueue}
              >
                Send Order To {actionData.queue} Queue
              </LoadingButton>
            </ProtectedContent>
          )}
          {showTriggerButton && (
            <ProtectedContent items={[permissions.AlpacaPermissions.sendToTransferQueue]}>
              <LoadingButton
                type="button"
                color="secondary"
                variant="outlined"
                loading={isTriggerLoading}
                onClick={onTriggerTransferCreatedToCompleted}
              >
                Trigger transfer created to completed
              </LoadingButton>
            </ProtectedContent>
          )}
          {showCheckJournalStatusButton && (
            <ProtectedContent items={[permissions.AlpacaPermissions.checkJournalStatus]}>
              <LoadingButton
                type="button"
                color="info"
                variant="outlined"
                loading={isCheckJournalStatusLoading}
                onClick={onCheckJournalStatus}
              >
                Check journal status
              </LoadingButton>
            </ProtectedContent>
          )}
        </Box>
        {children}
      </SectionCard>
      <AsyncDialog ref={asyncDialogRef} dialog={ConfirmationDialog} />
    </>
  );
}
